import React, { useState, useEffect } from 'react';
import * as planUtils from '../utils/plans';
import { useAuth } from '../contexts/AuthContext';

/**
 * SavePlanModal
 *
 * Props:
 *   isOpen         – boolean, controls visibility
 *   onClose        – called when user cancels
 *   planData       – { event_data, themes, selected_theme, shopping_items }
 *   currentPlanId  – string|null, existing plan ID if updating
 *   draftMode      – boolean; true = Save Draft (stay on screen), false = Save Plan (go to dashboard)
 *   onSave(planId) – called after successful save with the plan ID
 *   onDiscard      – optional; if provided, shows a "Discard" button (unsaved-changes flow)
 *   onRequestAuth  – called with the plan name when user isn't authenticated
 */
const SavePlanModal = ({
  isOpen,
  onClose,
  planData,
  currentPlanId,
  draftMode = false,
  onSave,
  onDiscard,
  onRequestAuth,
  sidebarCollapsed = false,
  isNavigationExit = false,
  isNewUnsavedPlan = false,
  saveButtonLabel,
  discardButtonLabel,
}) => {
  const { isAuthenticated, handleExpiredSession } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Derive existing plan name from planData (handles both field names)
  const existingName =
    planData?.event_data?.plan_name ||
    planData?.event_data?.event_type ||
    '';

  const [planName, setPlanName] = useState(existingName);

  // Re-sync when modal opens / planData changes
  useEffect(() => {
    if (isOpen) {
      setPlanName(existingName);
      setError('');
      setSuccess(false);
    }
  }, [isOpen, existingName]);

  if (!isOpen) return null;

  const hasPlanId = !!currentPlanId;
  const alreadyNamed = existingName.trim().length >= 3;

  const title = isNavigationExit
    ? 'Save before leaving?'
    : draftMode
    ? (onDiscard ? 'Save before leaving?' : 'Save Draft')
    : 'Save Plan';

  const subtitle = success
    ? (draftMode ? 'Draft saved!' : 'Plan saved successfully!')
    : isNavigationExit
    ? (isNewUnsavedPlan
        ? 'This event plan has not been saved yet. Save it as a draft before leaving, or discard it entirely.'
        : 'You have unsaved changes. Save them before leaving, or discard only your recent changes.')
    : onDiscard
    ? 'You have unsaved changes. Save or discard?'
    : draftMode
    ? 'Your progress will be saved so you can continue later.'
    : 'Your plan will be saved and accessible from "My Plans".';

  const handleSave = async () => {
    if (!isAuthenticated) {
      if (onRequestAuth) onRequestAuth(planName);
      return;
    }

    const trimmed = planName.trim();
    if (trimmed.length < 3) {
      setError('Plan name must be at least 3 characters.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess(false);

    try {
      const dataToSave = {
        ...planData,
        event_data: {
          ...planData?.event_data,
          plan_name: trimmed,
        },
      };

      const planId = currentPlanId;
      let result;
      if (planId) {
        result = await planUtils.updatePlan(planId, dataToSave);
      } else {
        result = await planUtils.createPlan(dataToSave);
      }

      const finalPlanId = result?.plan_id || planId;
      setSuccess(true);

      setTimeout(() => {
        if (onSave) onSave(finalPlanId);
        setSuccess(false);
        setSaving(false);
      }, 900);
    } catch (err) {
      console.error('Save error:', err);
      // Check for any auth/credentials failure from FastAPI (401) or the backend
      const isAuthError = (
        err.message === 'Not authenticated' ||
        err.message?.includes('401') ||
        err.message?.includes('Authentication') ||
        err.message?.toLowerCase().includes('credentials') ||
        err.message?.toLowerCase().includes('unauthorized')
      );
      if (isAuthError) {
        // Try a silent token refresh first — the access token may have just expired
        if (handleExpiredSession) {
          const result = await handleExpiredSession();
          if (result === 'refreshed') {
            // Token refreshed successfully — retry the save automatically
            setSaving(false);
            handleSave();
            return;
          }
        }
        // Refresh failed or not available — open login modal
        if (onRequestAuth) onRequestAuth(planName);
        return;
      }
      setError('Failed to save. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 sm:p-6"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-[min(calc(100vw-32px),560px)] max-h-[calc(100vh-32px)] overflow-y-auto p-6 sm:p-8 transform scale-100 transition-all duration-300 border border-gray-100"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-stone-100 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm">
            <svg className="w-8 h-8 text-zinc-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
            </svg>
          </div>
          <h2 className="text-2xl font-extrabold text-gray-900 mb-2 tracking-tight">{title}</h2>
          <p className="text-gray-500 text-sm max-w-sm mx-auto leading-relaxed">{subtitle}</p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3.5 bg-red-50 border border-red-200 rounded-2xl text-sm text-red-600 flex items-center gap-2">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            {error}
          </div>
        )}

        {/* Success state */}
        {success ? (
          <div className="text-center py-8">
            <div className="inline-flex items-center gap-2.5 text-green-600 font-bold text-xl">
              <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center text-green-500 shadow-inner">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              {draftMode ? 'Draft saved!' : 'Saved!'}
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Plan Name Section (inside a soft card) */}
            <div className="bg-gray-50 border border-gray-200/60 rounded-2xl p-4 sm:p-5">
              <label className="block text-sm font-bold text-gray-800 mb-1.5">
                Plan name <span className="text-red-500">*</span>
              </label>
              
              <input
                type="text"
                value={planName}
                onChange={(e) => setPlanName(e.target.value)}
                placeholder="e.g., Summer Birthday Bash"
                maxLength={50}
                autoFocus={typeof window !== 'undefined' ? window.innerWidth >= 768 : false}
                className={`w-full px-4 py-3 rounded-xl border outline-none transition-all ${
                  planName.trim().length < 3
                    ? 'border-stone-300 ring-4 ring-stone-50 focus:border-stone-400 focus:ring-stone-100 bg-white'
                    : 'border-gray-200 focus:ring-2 focus:ring-zinc-900 focus:border-transparent bg-white shadow-sm'
                }`}
              />

              {planName.trim().length < 3 ? (
                <p className="text-xs text-stone-600 mt-2 flex items-center gap-1.5 font-medium">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  Must be between 3 and 50 characters.
                </p>
              ) : (
                <p className="text-xs text-gray-500 mt-2 font-medium">
                  Name this draft so you can find it later.
                </p>
              )}

              {/* Already named hint */}
              {hasPlanId && alreadyNamed && (
                <p className="text-[10px] text-gray-400 mt-1.5 font-normal">
                  You can rename the plan here or keep the current name.
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              {/* Stay / Cancel (neutral outline) */}
              <button
                onClick={onClose}
                className="w-full sm:flex-1 py-3 px-4 border border-gray-300 bg-white text-gray-700 rounded-xl font-bold hover:bg-gray-50 active:bg-gray-100 transition-colors focus:outline-none"
              >
                {isNavigationExit ? 'Stay' : 'Cancel'}
              </button>

              {/* Discard (soft red outline/text) — only shown when navigating away with unsaved changes */}
              {onDiscard && (
                <button
                  onClick={onDiscard}
                  className="w-full sm:flex-1 py-3 px-4 border border-red-200 bg-red-50/20 text-red-600 rounded-xl font-bold hover:bg-red-50 hover:border-red-300 transition-colors focus:outline-none"
                >
                  {discardButtonLabel || 'Discard'}
                </button>
              )}

              {/* Save (primary action) */}
              {isAuthenticated && (
                <button
                  onClick={handleSave}
                  disabled={saving || planName.trim().length < 3}
                  title={planName.trim().length < 3 ? 'Name must be at least 3 characters' : ''}
                  className="w-full sm:flex-1 py-3 px-4 bg-zinc-900 text-white rounded-xl font-bold hover:bg-zinc-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md hover:shadow-lg focus:outline-none"
                >
                  {saving ? 'Saving…' : saveButtonLabel || (draftMode ? 'Save Draft' : 'Save')}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SavePlanModal;
